import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { IWebsiteWidgetConfig, WebsiteWidgetLayout } from '../../../interfaces/website_widget';
import { FormCheckbox } from '../../common/form_checkbox';
import { FormInput } from '../../common/form_input';
import { FormSelect } from '../../common/form_select';

type Props = {
    config: IWebsiteWidgetConfig;
    setRefreshPreview: any;
};

export default function WebsiteWidgetConfig({ config, setRefreshPreview }: Props) {
    return (
        <div className="ring-1 ring-brand_grey rounded-md sm:rounded-lg overflow-hidden">
            <h2 className="bg-brand_faint-blue text-md md:text-lg lg:text-xl p-3 sm:p-4 xl:px-6 font-bold flex items-center capitalize">
                <span className="px-3 py-2 rounded-xl bg-brand_primary text-white flex items-center justify-center text-xl font-bold me-2 min-w-20 whitespace-nowrap">Step 2</span> Configure {config.layout} review widget
            </h2>
            <div className="border-t border-brand_grey p-3 sm:p-4 xl:p-6 grid gap-4">
                <h3 className="block text-md md:text-lg font-bold">
                    General Options
                </h3>
                <div className="grid lg:grid-cols-2 gap-1.5 sm:gap-3">
                    <FormCheckbox 
                        name="showLogo"
                        label="Show NursingHomes.com Logo"
                    />
                    <FormCheckbox 
                        name="showFacilityName"
                        label="Show Facility Name"
                    />
                    <FormCheckbox 
                        name="showReviewScore"
                        label="Show Review Score"
                    />
                    <FormCheckbox 
                        name="showReviews"
                        label={<>Show Reviews <span className="ms-1 text-sm text-brand_grey-medium">(further options below)</span></>}
                    />
                    <FormCheckbox 
                        name="showReadAllReviewsLink"
                        label={<>Show <em className="mx-1">Read all XX reviews</em> Link</>}
                    />
                    <FormCheckbox 
                        name="showWriteReviewButton"
                        label={<>Show <em className="mx-1">Write a Review</em> Button</>}
                    />
                </div>    
            </div>
            {
                config.showReviews && (
                    <div className="border-t border-brand_grey p-3 sm:p-4 xl:p-6 grid gap-4">
                        <h3 className="block text-md md:text-lg font-bold">
                            Reviews Options
                        </h3>
                        <div className="grid gap-1.5 sm:gap-3 md:gap-5">
                            <div className="flex items-center gap-2">
                                Show
                                <FormInput
                                    name="reviewCount"
                                    type="number"
                                    min={1}
                                    max={50}
                                    inlineLabel={false}
                                    maxWidth="4rem"
                                />
                                <span>
                                    recent reviews <span className="text-sm text-brand_grey-medium">(max 50)</span>
                                </span>
                            </div>
                            <div className="flex items-center gap-2 whitespace-nowrap">
                                Show reviews with a rating of                                
                                <FormSelect
                                    name="minReviewRating"
                                    inlineLabel={false}
                                    isSearchable={false}
                                    maxWidth="13rem"
                                    onChange={() => setRefreshPreview(true)}
                                    options={[
                                        {
                                            label: 'Show all ratings',
                                            value: null
                                        },
                                        {
                                            label: '2 stars and above',
                                            value: 2
                                        },
                                        {
                                            label: '3 stars and above',
                                            value: 3
                                        },
                                        {
                                            label: '4 stars and above',
                                            value: 4
                                        },
                                        {
                                            label: '5 stars',
                                            value: 5
                                        },
                                    ]}
                                />  
                            </div>
                            <div className="ring-1 ring-brand_grey-light rounded-md p-4">
                                <h4 className="block text-md md:text-base font-medium mb-3">
                                    Slideshow
                                </h4>
                                <div className="grid gap-1.5 sm:gap-3">
                                    <FormCheckbox 
                                        name="slideShow"
                                        label="Display reviews as a slideshow"
                                    />
                                    {
                                        config.slideShow && (
                                            <div className="flex items-center gap-2">
                                            Initially, show
                                            <FormInput
                                                name="slideShowReviewsCount"
                                                type="number"
                                                min={1}
                                                max={4}
                                                inlineLabel={false}
                                                maxWidth="4rem"
                                            />
                                            <span>
                                                reviews <span className="text-sm text-brand_grey-medium">(max 4)</span>
                                            </span>
                                        </div>
                                        )
                                    }
                                </div>
                            </div>
                            <div className="ring-1 ring-brand_grey-light rounded-md p-4">
                                <h4 className="block text-md md:text-base font-medium mb-3">
                                    Individual Reviews
                                </h4>
                                <div className="grid gap-1.5 sm:gap-3">
                                    <FormCheckbox 
                                        name="showRatings"
                                        label="Show Star Rating"
                                    />
                                    <FormCheckbox 
                                        name="showDate"
                                        label="Show Date"
                                    />
                                    <FormCheckbox 
                                        name="showReviewSnippet"
                                        label={<>Show Snippet of Review <span className="mx-1 text-sm text-brand_grey-medium">(else full review text is shown)</span></>}
                                        disabled={config.layout === WebsiteWidgetLayout.Vertical && config.slideShow}
                                    />
                                    <FormCheckbox 
                                        name="showReviewerAndConnection"
                                        label="Show Reviewer Name &amp; Connection"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            <div className="relative border-t border-brand_grey">
                <FontAwesomeIcon icon={faChevronRight} className="absolute w-3.5 h-3.5 top-[14px] sm:top-[19px] xl:top-[27px] left-3 sm:left-4 xl:left-6" />
                <label htmlFor="optionsVertical" className="block ms-6 p-3 sm:p-4 xl:p-6 cursor-pointer link">
                    More Customization Options 
                </label>
                <input type="checkbox" className="hidden peer/optionsVertical" id="optionsVertical" onChange={(event) => event.stopPropagation()} />
                <div className="hidden peer-checked/optionsVertical:block">
                    <FontAwesomeIcon icon={faChevronDown} className="bg-white absolute w-3.5 h-3.5 top-[14px] sm:top-[19px] xl:top-[27px] left-3 sm:left-4 xl:left-6" />
                    <div className="p-3 sm:p-4 xl:p-6 !pt-0 grid gap-4">
                        <div className="grid gap-1.5 sm:gap-3 md:gap-5">
                            <div className="ring-1 ring-brand_grey-light rounded-md p-4">
                                <h4 className="block text-md md:text-base font-medium mb-3">
                                    Layout
                                </h4>
                                <div className="grid gap-1.5 sm:gap-3">
                                    <FormCheckbox 
                                        name="border"
                                        label="Border"
                                    />
                                    <div className="flex items-center gap-2 whitespace-nowrap">
                                        Background Color  
                                        <FormSelect
                                            name="backgroundColor"
                                            inlineLabel={false}
                                            isSearchable={false}
                                            maxWidth="12rem"
                                            onChange={() => setRefreshPreview(true)}
                                            options={[
                                                {
                                                    label: 'Transparent',
                                                    value: 'transparent'
                                                },
                                                {
                                                    label: 'White',
                                                    value: 'white'
                                                },
                                                {
                                                    label: 'Light Blue',
                                                    value: 'light-blue'
                                                },
                                                {
                                                    label: 'Light Green',
                                                    value: 'light-green'
                                                },
                                                {
                                                    label: 'Light Grey',
                                                    value: 'light-grey'
                                                },
                                            ]}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="ring-1 ring-brand_grey-light rounded-md p-4">
                                <h4 className="block text-md md:text-base font-medium mb-3">
                                    Width
                                </h4>
                                <div className="grid gap-1.5 sm:gap-3">
                                    <FormCheckbox 
                                        name="widthFull"
                                        label={<>Fills available space <span className="ms-1 text-sm text-brand_grey-medium">(responsive to browser width)</span></>}
                                    />
                                    {
                                        !config.widthFull && (
                                            <div className="flex items-center gap-2">
                                                Set width
                                                <FormInput
                                                    name="width"
                                                    type="number"
                                                    inlineLabel={false}
                                                    maxWidth="6rem"
                                                    min={308}
                                                    suffix="px"
                                                />
                                                <span>
                                                    <span className="text-sm text-brand_grey-medium">
                                                        (minimum width 308px)
                                                    </span>
                                                </span>
                                            </div>
                                        )
                                    }
                                </div>
                            </div>
                            <div className="ring-1 ring-brand_grey-light rounded-md p-4">
                                <h4 className="block text-md md:text-base font-medium mb-3">
                                    Outer Margin
                                </h4>
                                <div className="flex flex-wrap gap-1.5 sm:gap-5">
                                    <div className="flex items-center gap-2">
                                        Top
                                        <FormInput
                                            name="marginTop"
                                            type="number"
                                            inlineLabel={false}
                                            maxWidth="4rem"
                                            min={0}
                                            suffix="px"
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        Right
                                        <FormInput
                                            name="marginRight"
                                            type="number"
                                            inlineLabel={false}
                                            maxWidth="4rem"
                                            min={0}
                                            suffix="px"
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        Bottom
                                        <FormInput
                                            name="marginBottom"
                                            type="number"
                                            inlineLabel={false}
                                            maxWidth="4rem"
                                            min={0}
                                            suffix="px"
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        Left
                                        <FormInput
                                            name="marginLeft"
                                            type="number"
                                            inlineLabel={false}
                                            maxWidth="4rem"
                                            min={0}
                                            suffix="px"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="ring-1 ring-brand_grey-light rounded-md p-4">
                                <h4 className="block text-md md:text-base font-medium mb-3">
                                    Links
                                </h4>
                                <div className="grid gap-1.5 sm:gap-3">
                                    <FormCheckbox 
                                        name="noFollowLinks"
                                        label="Add a 'nofollow' tag to links"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
